import { z } from "zod";
import { BillFiltersSchema } from "@common/contracts/bill.contract";
import { InvoiceFiltersSchema } from "@common/contracts/invoice.contract";
import { BillSource, BillStatus } from "@common/types/bill";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import {
    InvoiceLineItemKind,
    InvoiceSource,
    InvoiceStatus,
} from "@common/types/invoice";
import { accountOverrideEntryResponseSchema } from "../accounting/account-override.schema";
import { classAssignmentSchema } from "../../class.schema";
import { counterpartySchema } from "../../counterparty.schema";

const suggestedAccrualMatchEntitySchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    iconColor: z.string(),
    workspaceId: z.string(),
    logo: z.string().nullable(),
});

const suggestedAccrualMatchFinancialAccountSchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    sourceType: z.nativeEnum(FinancialAccountSourceType),
    accountNumberMask: z.string().nullable(),
    entityId: z.number().nullable(),
    order: z.number(),
    entity: suggestedAccrualMatchEntitySchema.nullable().optional(),
});

const suggestedInvoiceLineItemSchema = z.object({
    id: z.string().uuid(),
    amount: z.number(),
    matchedAmount: z.number(),
    unitAmount: z.number().nullable().optional(),
    currency: z.string(),
    description: z.string().nullable(),
    quantityDecimal: z.number().nullable(),
    source: z.nativeEnum(InvoiceSource),
    sourceId: z.string(),
    accountId: z.string().uuid().nullable().optional(),
    accountsReceivableId: z.string().uuid().nullable().optional(),
    accountOverrides: z.array(accountOverrideEntryResponseSchema).optional(),
    accountsReceivableOverrides: z
        .array(accountOverrideEntryResponseSchema)
        .optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    invoiceId: z.string().uuid(),
    kind: z.nativeEnum(InvoiceLineItemKind),
    rowId: z.string().nullable(),
});

const suggestedBillLineItemSchema = z.object({
    id: z.string().uuid(),
    amount: z.number(),
    matchedAmount: z.number(),
    unitAmount: z.number().nullable().optional(),
    currency: z.string(),
    description: z.string().nullable(),
    quantityDecimal: z.number().nullable(),
    sourceId: z.string(),
    accountId: z.string().uuid().nullable().optional(),
    accountsPayableId: z.string().uuid().nullable().optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    billId: z.string().uuid(),
});

export const suggestedAccrualMatchInvoiceSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    due: z.coerce.date().nullable(),
    paidAt: z.coerce.date().nullable(),
    uncollectableAt: z.string().nullable(),
    voidedAt: z.string().nullable(),
    total: z.number(),
    subtotal: z.number(),
    tax: z.number(),
    discount: z.number(),
    currency: z.string(),
    counterparty: counterpartySchema.nullable().optional(),
    description: z.string().nullable(),
    memo: z.string().nullable(),
    status: z.nativeEnum(InvoiceStatus),
    source: z.nativeEnum(InvoiceSource),
    sourceId: z.string().nullable(),
    isSubscription: z.boolean(),
    lines: z.array(suggestedInvoiceLineItemSchema).optional(),
    entityId: z.number(),
    financialAccountId: z.number().nullable(),
    financialAccount: suggestedAccrualMatchFinancialAccountSchema
        .nullable()
        .optional(),
    invoiceNumber: z.string().nullable(),
    financialDocumentIds: z.array(z.number()).optional(),
});

export const suggestedAccrualMatchBillSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    due: z.coerce.date().nullable(),
    paidAt: z.coerce.date().nullable(),
    total: z.number(),
    subtotal: z.number(),
    tax: z.number(),
    discount: z.number(),
    currency: z.string(),
    counterparty: counterpartySchema.nullable().optional(),
    description: z.string().nullable(),
    memo: z.string().nullable(),
    status: z.nativeEnum(BillStatus),
    source: z.nativeEnum(BillSource),
    sourceId: z.string().nullable(),
    lines: z.array(suggestedBillLineItemSchema).optional(),
    entityId: z.number(),
    financialAccountId: z.number().nullable(),
    financialAccount: suggestedAccrualMatchFinancialAccountSchema
        .nullable()
        .optional(),
    invoiceNumber: z.string().nullable(),
    financialDocumentIds: z.array(z.number()).optional(),
});

export const suggestedAccrualMatchInvoicesResponseSchema = z.object({
    total: z.number(),
    pageCount: z.number(),
    data: z.array(suggestedAccrualMatchInvoiceSchema),
});

export const suggestedAccrualMatchBillsResponseSchema = z.object({
    total: z.number(),
    pageCount: z.number(),
    data: z.array(suggestedAccrualMatchBillSchema),
});

const suggestedAccrualMatchDocumentSortSchema = z.object({
    field: z.string(),
    order: z.enum(["ASC", "DESC"]),
});

const suggestedAccrualMatchDocumentQueryBaseSchema = z.object({
    page: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().positive().max(100).default(20),
    scope: z.enum(["suggested", "all"]).default("suggested"),
    sort: suggestedAccrualMatchDocumentSortSchema.optional(),
});

export const suggestedAccrualMatchInvoicesQuerySchema =
    suggestedAccrualMatchDocumentQueryBaseSchema.extend({
        filters: InvoiceFiltersSchema.optional(),
    });

export const suggestedAccrualMatchBillsQuerySchema =
    suggestedAccrualMatchDocumentQueryBaseSchema.extend({
        filters: BillFiltersSchema.optional(),
    });

export type SuggestedAccrualMatchInvoicesResponse = z.infer<
    typeof suggestedAccrualMatchInvoicesResponseSchema
>;

export type SuggestedAccrualMatchBillsResponse = z.infer<
    typeof suggestedAccrualMatchBillsResponseSchema
>;

export type SuggestedAccrualMatchInvoicesQuery = z.infer<
    typeof suggestedAccrualMatchInvoicesQuerySchema
>;

export type SuggestedAccrualMatchBillsQuery = z.infer<
    typeof suggestedAccrualMatchBillsQuerySchema
>;
